import useActiveNavigation from '@Guidelines/Shell/AppShell/Hooks/useActiveNavigation';
import type { VisibleNavigationEntry } from '@Guidelines/Shell/AppShell/Hooks/useVisibleNavigationMenu';
import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import type * as React from 'react';
import { Link } from 'react-router-dom';

const linkStyle = css`
	display: flex;
	align-items: center;
	gap: 0.75rem;
	min-height: 2.75rem;
	padding: 0 0.75rem;
	border-radius: ${theme.radii.small};
	color: ${theme.navigationMenu.item.text};
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	font-weight: ${theme.fontWeights.medium};
	line-height: 1.25;
	text-decoration: none;
	transition:
		background-color ${theme.motion.duration},
		color ${theme.motion.duration};

	svg {
		flex-shrink: 0;
		color: ${theme.navigationMenu.item.icon};
		transition: color ${theme.motion.duration};
	}

	&:hover {
		background-color: ${theme.navigationMenu.item.hoverBackground};
		color: ${theme.navigationMenu.item.hoverText};
	}

	&:focus-visible {
		outline: 2px solid ${theme.colors.accent};
		outline-offset: 2px;
	}
`;

const activeLinkStyle = css`
	background-color: ${theme.navigationMenu.itemActive.background};
	color: ${theme.navigationMenu.itemActive.text};
	font-weight: ${theme.fontWeights.semibold};

	svg {
		color: ${theme.navigationMenu.itemActive.icon};
	}

	&:hover {
		background-color: ${theme.navigationMenu.itemActive.background};
		color: ${theme.navigationMenu.itemActive.text};
	}
`;

const labelStyle = css`
	min-width: 0;
	overflow-wrap: anywhere;
`;

type Props = {
	readonly item: VisibleNavigationEntry;
};

/** One menu entry: icon and label linking to the entry's first accessible destination. */
const NavigationMenuItem: React.FC<Props> = ({ item }) => {
	const activeNavigation = useActiveNavigation();
	const isActive = activeNavigation?.entry.id === item.entry.id;
	const Icon = item.entry.icon;

	return (
		<Link
			to={item.href}
			css={[linkStyle, isActive ? activeLinkStyle : null]}
			aria-current={isActive ? 'page' : undefined}
		>
			<Icon size={18} aria-hidden="true" />
			<span css={labelStyle}>{item.entry.label}</span>
		</Link>
	);
};

export default NavigationMenuItem;
